import { Injectable } from '@angular/core';
import { FilterObjectAdapter } from '@app/adapters/_index';
import { ApiService } from '../apiService.service';
import { AdditionalField, FilterField } from '@app/models/_index';
import { FilterFieldInterface } from '@app/interfaces/_index';
import { environment } from '@environments/environment';

export interface CatalogParams {
  query: string;
  role: string[];
  category_id: string[];
  topic: string[];
  learningType: string[];
  course: string[];
  api_key: string;
}

interface FieldValueCount {
  value: string;
  count: number;
}
@Injectable({
  providedIn: 'root',
})
export class FilterCatalogItemService {
  private searchCoursesURL = `${environment.beyondApi.url}/v2/getcourses`;

  constructor(private apiService: ApiService, private adapter: FilterObjectAdapter) {}

  /**
   * Filter the Catalog Items data.
   * @param params - basic param structure
   * @returns the catalogObject and used the adpeter to filter only the required fields.
   */
  getFilterCatalogItems(params: CatalogParams): Promise<FilterField[]> {
    return this.apiService
      .post(this.searchCoursesURL, JSON.stringify(params))
      .then((response: any) => {
        const catalogObjects: FilterField[] = [];
        if (response && response.body && response.body.data) {
          response.body.data.forEach((catalog: FilterFieldInterface) => {
            catalogObjects.push(this.adapter.adapt(catalog));
          });
        }
        return Promise.resolve(catalogObjects);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  }

  /**
   * Function to get all the additional Field values based on the fieldTitle.
   * NOTE : You need to refer the sample course structure to check what fieldTitle params you can pass to get the appropriate response.
   * @param fieldTitle - pass any additionalField value title to get the value for it. Need to refere the response
   * @returns the list of array values based on fieldType like topics, roles, learningType etc.
   */
  async getAllObjects(fieldTitle: string): Promise<FieldValueCount[]> {
    const courseParams = {
      query: '',
      role: [],
      category_id: [],
      topic: [],
      learningType: [],
      course: [],
      api_key: `${environment.search_api}`,
    };

    try {
      const results = await this.getFilterCatalogItems(courseParams);
      // filtering out self-study and subscription roles and topics, but we don't want to filter out the learning types
      const filteredResults = results.filter(
        (item) =>
          fieldTitle == 'Learning Type' ||
          (!item.name.includes('Self-Study') && !item.name.includes('Subscription'))
      );

      const countMap = new Map<string, number>();

      filteredResults.forEach((allresults) => {
        if (allresults && allresults && Array.isArray(allresults.additional_fields)) {
          // Access additional_fields from each object
          const additionalFields = allresults.additional_fields;

          // Filter the fields where title matches the provided fieldTitle
          const field = additionalFields.find((val: AdditionalField) => val.title === fieldTitle);

          // If found, split the value by commas and add each item to the set
          if (field && field.value) {
            const splitValues = field.value
              .split(',')
              .map((value: string) => value?.trim())
              .filter((value) => value.length > 0);
            splitValues.forEach((value: string) => {
              const currentCount = countMap.get(value) || 0;
              countMap.set(value, currentCount + 1);
            });
          }
        }
      });

      const resultArray = Array.from(countMap.entries()).map(([value, count]) => ({
        value,
        count,
      }));

      resultArray.sort((a, b) => a.value.localeCompare(b.value));
      return Promise.resolve(resultArray);
    } catch (error) {
      return Promise.reject('Get all Search Objects error' + error);
    }
  }
}
